// Type guard to check if object has an 'id' property

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import '../../../../i18n'

import { isLayout } from '@alphabait/ui/form'
import cx from 'classnames'

import { DropboxLogo } from '@phosphor-icons/react'
import { AlphaTypes } from '../../custom-types'
import { getTemplates } from '../available'
import { Droppable } from '../components/Droppable'

import { useTranslation } from 'react-i18next'
import QuestionnaireOptions from './quesitonnaire-options'
import QuestionnaireEditor from './questionnaire-editor'

interface DndQuestionnaireProps {
  reset: (values: AlphaTypes[]) => void
  values: AlphaTypes[]
  render: (fields: AlphaTypes[]) => React.ReactNode
  setFields: (fields: AlphaTypes[]) => void
  campaignId?: string
}

export function DndQuestionnaire({
  reset,
  values,
  render,
  setFields,
  campaignId,
}: DndQuestionnaireProps) {
  const { i18n, t } = useTranslation()
  const [templates, setTemplates] = useState(getTemplates())

  // Update templates when language changes
  useEffect(() => {
    setTemplates(getTemplates())
  }, [i18n.language])

  useEffect(() => reset(values), [reset, values])

  const formFields = values ?? []
  const [selectedField, setSelectedField] = useState<AlphaTypes | null>(null)

  const [activeDragItem, setActiveDragItem] = useState<AlphaTypes | null>(null)

  const location = useLocation()

  useEffect(() => {
    setSelectedField(null)
  }, [location])

  // Configure sensors for drag and drop interactions
  const sensors = useSensors(
    useSensor(MouseSensor, {
      // Decrease activation distance to make it easier to start dragging
      activationConstraint: { distance: 2 },
    }),
    useSensor(PointerSensor, {
      // Lower activation distance for better touch/pointer support
      activationConstraint: { distance: 2 },
    }),
    useSensor(TouchSensor, {
      // Improve touch support
      activationConstraint: { delay: 100, tolerance: 5 },
    }),
  )

  // Event handlers for drag operations
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event

    // Find the item being dragged to display in the overlay
    const activeContainer = active.data.current?.sortable?.containerId
    const index = active.data.current?.sortable?.index

    if (!activeContainer || index === undefined) return

    // Set the active drag item for the overlay
    if (activeContainer === 'formContainer') {
      // Make a deep copy to avoid reference issues
      const formField = formFields[index] as AlphaTypes
      setActiveDragItem(formField)
    } else if (activeContainer === 'templateContainer') {
      // Make a deep copy to avoid reference issues
      const template = templates[index] as AlphaTypes
      setActiveDragItem(template)
    }

    // Add a class to the body to indicate dragging is active
    document.body.classList.add('dragging-active')
  }

  const handleDragCancel = () => {
    // Reset active drag state
    setActiveDragItem(null)

    // Remove the dragging class when drag is cancelled
    document.body.classList.remove('dragging-active')
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    // Reset active drag state
    setActiveDragItem(null)

    // Remove the dragging class when drag ends
    document.body.classList.remove('dragging-active')

    if (!over) {
      return
    }

    if (active.id !== over.id) {
      const activeContainer = active.data.current?.sortable?.containerId
      // Make sure we get the correct container for the target
      // If over.data.current?.sortable?.containerId is undefined, use over.id as fallback
      const overContainer =
        over.data.current?.sortable?.containerId ||
        (typeof over.id === 'string' && over.id.includes('Container')
          ? over.id
          : 'formContainer')

      if (!activeContainer) {
        return
      }

      // Get source index
      const moveFrom = active.data.current?.sortable?.index
      // Get target index, being careful about potential undefined values
      let moveTo = over.data.current?.sortable?.index

      // If target index is undefined but we're dropping onto a container,
      // place at the end of that container
      if (moveTo === undefined && overContainer === 'formContainer') {
        moveTo = formFields.length
      }

      if (moveFrom === undefined || moveTo === undefined) {
        return
      }

      let updatedFields = [...formFields]

      // Handle reordering within the form container
      if (
        activeContainer === overContainer &&
        activeContainer === 'formContainer'
      ) {
        // Get the field being moved (make a deep copy to avoid reference issues)
        const field = JSON.parse(JSON.stringify(formFields[moveFrom]))

        // For within-container moves, always use a direct array manipulation approach
        // to ensure exact positioning regardless of index shifts
        updatedFields = [...formFields] // Create a new array to avoid mutation

        // Remove item from source position
        updatedFields.splice(moveFrom, 1)

        // Insert at destination position
        updatedFields.splice(moveTo, 0, field)
      }
      // Handle dragging from template container to form container
      else if (
        activeContainer === 'templateContainer' &&
        overContainer === 'formContainer'
      ) {
        // Create a copy of the template field with a unique ID
        const field = JSON.parse(JSON.stringify(templates[moveFrom]))
        const uniqueId = `field-${Date.now()}-${Math.floor(
          Math.random() * 1000,
        )}`
        field.id = uniqueId

        if (field.uiOptions?.label) {
          field.name = field.uiOptions.label
        }

        if (field.uiOptions?.label) {
          field.name = field.uiOptions.label
        }

        // Mark this field as newly added so we can highlight it
        field.isNewlyAdded = true

        // For template-to-form moves, use direct array insertion for precise positioning
        updatedFields = [...formFields] // Create a new array to avoid mutation

        // Check if we're dropping directly onto an existing field (rather than between fields)
        const isDropOnExistingField =
          typeof over.id === 'string' && !over.id.includes('Container')

        // If we're dropping directly on an existing field, always add to the end
        // This solves the issue where new items would be placed before existing items
        if (isDropOnExistingField) {
          // Add to the end of the array
          updatedFields.push(field)
        }
        // Otherwise follow normal positioning logic
        else if (formFields.length === 0 || moveTo >= formFields.length) {
          // If this is the first item or dropping at the end, place at the end
          updatedFields.push(field)
        } else {
          // Otherwise, place it at the specified position
          updatedFields.splice(moveTo, 0, field)
        }

        // Note: We don't remove the item from addTemplate since we want it to remain available
      }

      setFields(updatedFields)
    }
  }

  // Function to handle field selection for configuration
  const handleFieldSelect = (field: AlphaTypes) => {
    // Check if the field has the required properties of InputField
    if ('name' in field && 'type' in field) {
      // It has the required properties of InputField
      setSelectedField(field)
    } else {
      // Handle the case where the field doesn't match InputField type
      console.warn('Selected field is not compatible with InputField type')
      setSelectedField(null)
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
    >
      <div className="flex justify-between w-full gap-6 mt-12">
        <div
          className={cx(
            formFields.length > 0 ? '' : 'rounded-lg border border-border',
            'w-3/4 p-4 bg-white',
          )}
        >
          {/* Use Droppable component for the form container to enable drag and drop */}
          <div className="min-h-[500px] border-0 rounded-lg p-4 bg-white">
            {formFields.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full py-10 !text-gray-300">
                <div className="relative mb-6">
                  <DropboxLogo size={32} />
                </div>
                <p className="text-center !text-gray-300">
                  {t('questionnaire_builder.drag_and_drop_question')}
                </p>
                <p className="text-center !text-gray-300">
                  {t('questionnaire_builder.blocks_section')}
                </p>
              </div>
            )}
            <Droppable
              id="formContainer"
              items={formFields}
              renderItem={(field: AlphaTypes) => {
                const previewField =
                  !isLayout(field) &&
                    'type' in field &&
                    field.type === 'custom' &&
                    'customControlType' in field &&
                    field.customControlType === 'picture-choice'
                    ? {
                      ...field,
                      previewMode: true,
                      questionnaireMode: false,
                      // If we need to add campaign ID, we can do it here without modifying the original
                      ...(campaignId ? { campaignId } : {}),
                    }
                    : field

                return (
                  <div
                    onClick={() => handleFieldSelect(field)}
                    className="mb-4 px-2 flex bg-white rounded-lg border border-white hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
                  >
                    <span className="m-2 pr-4">⋮⋮</span>
                    {render([previewField])}
                  </div>
                )
              }}
            />
          </div>
        </div>

        {/* Right column: Components and Configuration */}
        <div className="components-config-container w-96 p-4 bg-gray-50 rounded-lg">
          {selectedField ? (
            <QuestionnaireEditor
              key={'id' in selectedField ? selectedField.id : undefined}
              selectedField={selectedField}
              setSelectedField={setSelectedField}
              setFields={setFields}
              formFields={formFields}
            />
          ) : (
            <QuestionnaireOptions components={templates} />
          )}
        </div>
      </div>
      {/* Drag overlay for visual feedback during drag */}
      {createPortal(
        <DragOverlay adjustScale={true}>
          {activeDragItem && (
            <div
              className="drag-overlay-item"
              style={{
                opacity: 0.8,
                backgroundColor: 'white',
                padding: '8px',
              }}
            >
              {render([activeDragItem])}
            </div>
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  )
}
